// libraries
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

// Enable custom parsing
dayjs.extend(customParseFormat)

// phone
export function tel(str: string) {
	return (
		'tel:' + str.replace(/[^0-9]/g, '')
	)
}

// email
export function mailto(str: string) {
	return (
		'mailto:' + str
	)
}

// limit characters
export function limitCharacters(text: string, limit: number) {
    if (text.length <= limit) {
        return text
    } else {
        return text.slice(0, limit) + '...'
    }
}

// slugify
export function slugify(str: string) {
    return String(str)
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
}

// first char
export function firstChar(str: string) {
    return str.charAt(0) || ''
}

// placeholder
export const placeholder = (theme = 'dark') => {
    const colors = theme === 'light' 
        ? { start: '#fff', middle: '#eee', end: '#fff' } 
        : { start: '#333', middle: '#222', end: '#333' }

    const shimmer = `
        <svg width='1' height='1' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'>
            <defs>
                <linearGradient id='g'>
                    <stop stop-color='${colors.start}' offset='20%' />
                    <stop stop-color='${colors.middle}' offset='50%' />
                    <stop stop-color='${colors.end}' offset='70%' />
                </linearGradient>
            </defs>
            <rect width='1' height='1' fill='${colors.start}' />
            <rect id='r' width='1' height='1' fill='url(#g)' />
            <animate xlink:href='#r' attributeName='x' from='-1' to='1' dur='1s' repeatCount='indefinite'  />
        </svg>
    `

    return typeof window === 'undefined' ? Buffer.from(shimmer).toString('base64') : window.btoa(shimmer)
}

// get all focusable elements inside the container
export const getFocusableElements = (container: HTMLElement) => {
    return container.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    )
}

// get all focusable elements outside the container
export const getFocusableElementsOutside = (container: HTMLElement | null) => {
    if (!container) {
        return []
    }
    
    const allFocusableElements = document.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    )

    // filter out elements that are inside the container
    return Array.from(allFocusableElements).filter(
        (element) => !container.contains(element)
    )
}

export const formatDate = (date: string) => {
	// Try multiple parsing strategies for different date formats
	const formats = [
		'YYYY-MM-DD',           // 2025-07-24
		'DD/MM/YYYY',           // 24/07/2025
		'MM/DD/YYYY',           // 07/24/2025
		'DD-MM-YYYY',           // 24-07-2025
		'MM-DD-YYYY',           // 07-24-2025
		'DD.MM.YYYY',           // 24.07.2025
		'YYYY/MM/DD',           // 2025/07/24
		'DD MMM YYYY',          // 24 Jul 2025
		'DD MMMM YYYY',         // 24 July 2025
		'MMM DD, YYYY',         // Jul 24, 2025
		'MMMM DD, YYYY',        // July 24, 2025
	]

	let parsedDate = null

	// First try with no specific format (for ISO strings and other standard formats)
	parsedDate = dayjs(date)
	if (parsedDate.isValid()) {
		return parsedDate.format('DD.MM.YY')
	}

	// Try each format
	for (const format of formats) {
		parsedDate = dayjs(date, format, true) // strict parsing
		if (parsedDate.isValid()) {
			return parsedDate.format('DD.MM.YY')
		}
	}

	// Handle special cases like "24th, july - 25"
	const cleanedDate = date
		.replace(/(\d+)(st|nd|rd|th)/gi, '$1') // Remove ordinal suffixes
		.replace(/[,-]/g, ' ')                 // Replace commas and dashes with spaces
		.replace(/\s+/g, ' ')                  // Normalize multiple spaces
		.trim()

	// Try parsing the cleaned date
	for (const format of formats) {
		parsedDate = dayjs(cleanedDate, format, true)
		if (parsedDate.isValid()) {
			return parsedDate.format('DD.MM.YY')
		}
	}

	// If all else fails, return the original string or a fallback
	console.warn(`Could not parse date: ${date}`)
	return date
}

// format the number with thousand separators (spaces)
export function formatNumber(value: number) {
    const num = Math.floor(+value)
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export function capitalizeFirstLetter(str: string) {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getQueryParam(param: string): string | null {
    if (typeof window === 'undefined') return null;

    const query = window.location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=');
        if (decodeURIComponent(pair[0]) === param) {
            return decodeURIComponent(pair[1]);
        }
    }
    return null;
}
